/**
 * Provides an immediately renderable baseline while Firestore loads or is temporarily unavailable.
 * These values are also mirrored by the production seed script and are intentionally easy to replace.
 */
import type { PortfolioSnapshot } from '../../domain/entities/portfolio';

/** Builds replaceable demo media URLs until real app artwork is uploaded and linked in Firestore. */
const projectMedia = (title: string, background: string, foreground: string) => ({
  imageUrl: `https://placehold.co/1200x675/${background}/${foreground}/png?text=${encodeURIComponent(title)}+App`,
  screenshots: ['Home', 'Explore', 'Details', 'Activity', 'Insights', 'Profile', 'Settings', 'Search', 'Notifications'].map((screen, index) => ({
    url: `https://placehold.co/390x844/${background}/${foreground}/png?text=${encodeURIComponent(`${title}\n${screen}`)}`,
    alt: `${title} ${screen.toLowerCase()} screen`,
    caption: `${screen} screen`,
    order: index + 1,
  })),
});

export const fallbackPortfolio: PortfolioSnapshot = {
  profile: {
    brandName: 'Labibur',
    name: 'Labibur',
    title: 'Flutter Developer',
    bio: 'Flutter Developer with 1+ year of professional experience building polished, reliable mobile applications. Skilled in Flutter, Dart, clean architecture, Firebase, REST APIs and production delivery for Android and iOS.',
    shortBio:
      'I turn product ideas into thoughtful Flutter experiences with clean architecture, responsive interfaces and dependable Firebase backends.',
    location: 'Dhaka, Bangladesh',
    email: 'contact.labibur@gmail.com',
    availability: 'Available for selected projects',
    cvUrl: '',
    avatarUrl: '/assets/labibur.webp',
    logoUrl: '/favicon.svg',
    githubUrl: 'https://github.com/labib-ur-rahman',
    linkedinUrl: 'https://linkedin.com/in/labib-ur-rahman',
    stats: [
      { label: 'Apps Published', value: 5, suffix: '+' },
      { label: 'Years Experience', value: 1, suffix: '+' },
      { label: 'Completed Apps', value: 15, suffix: '+' },
    ],
  },
  siteContent: {
    navigation: [
      { label: 'About', href: '/about' },
      { label: 'Projects', href: '/projects' },
      { label: 'Contact', href: '/contact' },
    ],
    footerPages: [
      { label: 'Home', href: '/' },
      { label: 'About', href: '/about' },
      { label: 'Projects', href: '/projects' },
      { label: 'Contact', href: '/contact' },
    ],
    copy: {
      skipToContent: 'Skip to content',
      hireMe: 'Hire Me',
      heroGreeting: "Hi, I'm",
      viewWork: 'View My Work',
      downloadCv: 'Download CV',
      aboutMe: 'About Me',
      findMeOn: 'Find me on',
      portfolioLabel: 'Portfolio',
      publishedTitle: 'Published',
      publishedAccent: 'Apps',
      publishedDescription:
        'Production-minded Flutter products shaped for real users, clear workflows and dependable delivery.',
      viewAllProjects: 'View All Projects',
      expertiseLabel: 'Expertise',
      skillsTitle: 'Skills & Stack',
      skillsDescription:
        'A production-ready mobile toolkit tuned for clear architecture, speed and a polished user experience.',
      additionalSkillsTitle: 'Additional Skills',
      careerLabel: 'Career',
      experienceTitle: 'Work',
      experienceAccent: 'Experience',
      experienceDescription:
        'Building reliable mobile software through product thinking and focused engineering.',
      aboutLabel: 'About me',
      aboutTitle: 'Building great products',
      aboutAccent: 'for real users.',
      aboutExperienceDescription:
        'A practical journey through mobile product delivery and continuous improvement.',
      aboutSkillsDescription:
        'The technologies and engineering habits I use to ship dependable Flutter products.',
      educationLabel: 'Education',
      educationTitle: 'Learning',
      educationAccent: 'Background',
      educationDescription:
        'A foundation strengthened through project work, documentation and deliberate practice.',
      projectsKicker: 'My work',
      projectsTitle: 'Projects',
      projectsDescription: 'Production apps and focused experiments built with Flutter.',
      publishedGroupTitle: 'Published Apps',
      openSourceGroupTitle: 'Open Source Projects',
      personalGroupTitle: 'Personal Projects',
      detailsLabel: 'Details',
      allProjectsLabel: 'All Projects',
      featuredLabel: 'Featured',
      screenshotsLabel: 'Screenshots',
      appPreviewTitle: 'App Preview',
      featuresTitle: 'Core Features',
      projectNotFound: 'Project not found',
      backToProjects: 'Back to Projects',
      contactBadge: 'Contact',
      contactTitle: "Let's Talk",
      contactDescription:
        'Open to Flutter roles, freelance work and interesting product collaborations.',
      formTitle: 'Send a Message',
      formDescription: 'Fill out the form below or email',
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      subjectLabel: 'Subject',
      subjectPlaceholder: "What's this about?",
      messageLabel: 'Message',
      messagePlaceholder: 'Tell me about your project...',
      sendMessage: 'Send Message',
      sendingMessage: 'Sending...',
      contactSuccess: 'Thanks — your message was emailed and safely recorded.',
      contactError: 'The message could not be sent. Please email me directly.',
      emailDirectly: 'Email directly',
      ctaBadge: 'Get in touch',
      ctaTitle: "Let's Build Something",
      ctaAccent: 'Amazing',
      ctaDescription:
        'Open to Flutter roles, freelance products and collaborations with thoughtful teams.',
      ctaDetails:
        'I typically respond within 24 hours. Feel free to reach out about your app, role or product idea.',
      emailMe: 'Email me',
      sendEmail: 'Send Email',
      contactForm: 'Contact Form',
      footerDescription: 'crafting dependable, production-ready mobile experiences.',
      footerPagesTitle: 'Pages',
      footerSocialTitle: 'Social',
      footerButton: 'Get in Touch',
      footerCredit: 'Made with care using React & Firebase',
    },
    seo: {
      title: 'Labibur — Flutter Developer',
      description:
        'Flutter developer portfolio featuring production mobile apps, clean architecture and Firebase.',
    },
    additionalSkills: [
      'Flutter',
      'Dart',
      'GetX',
      'Riverpod',
      'BLoC',
      'REST APIs',
      'Agora',
      'Firebase',
      'Firestore',
      'FCM',
      'AI Integration',
      'Hive',
      'Git',
      'Google Maps SDK',
      'Feature-based Clean Architecture',
    ],
  },
  projects: [
    {
      id: 'commerce-flow',
      slug: 'commerce-flow',
      title: 'Commerce Flow',
      eyebrow: 'Retail toolkit',
      summary:
        'A production-ready commerce experience with catalog discovery, realtime order tracking and a streamlined checkout flow.',
      description:
        'Commerce Flow helps customers discover products, complete secure purchases and follow every order from confirmation to delivery. The modular Flutter codebase keeps catalog, checkout and fulfillment features independently maintainable.',
      kind: 'published',
      platform: 'Android & iOS',
      platforms: ['Android', 'iOS'],
      tags: ['Flutter', 'Firebase', 'Payments', 'Maps'],
      features: [
        'Searchable product catalog with saved filters',
        'Secure checkout and payment-state handling',
        'Realtime order tracking and push notifications',
        'Responsive layouts for phones and tablets',
      ],
      featured: true,
      order: 1,
      stores: [
        { kind: 'play-store', label: 'Play Store', url: 'https://play.google.com/store/apps' },
        { kind: 'app-store', label: 'App Store', url: 'https://apps.apple.com/' },
      ],
      liveUrl: '',
      githubUrl: '',
      ...projectMedia('Commerce Flow', 'fff0df', 'b6531a'),
      theme: { start: '#f39a48', end: '#e06820', ink: '#1a1208' },
    },
    {
      id: 'care-connect',
      slug: 'care-connect',
      title: 'Care Connect',
      eyebrow: 'Health platform',
      summary:
        'A calm appointment and tele-consultation app with realtime scheduling, secure profiles and notification workflows.',
      description:
        'Care Connect gives patients one calm place to find specialists, book appointments and join secure remote consultations. Reliable status updates keep both patients and care teams synchronized.',
      kind: 'published',
      platform: 'Android & iOS',
      platforms: ['Android', 'iOS'],
      tags: ['Flutter', 'Agora', 'FCM', 'REST API'],
      features: [
        'Doctor discovery and appointment scheduling',
        'Secure audio and video consultation sessions',
        'Automated reminders through push notifications',
        'Patient profile and visit history',
      ],
      featured: true,
      order: 2,
      stores: [
        { kind: 'play-store', label: 'Play Store', url: 'https://play.google.com/store/apps' },
        { kind: 'app-store', label: 'App Store', url: 'https://apps.apple.com/' },
      ],
      liveUrl: '',
      githubUrl: '',
      ...projectMedia('Care Connect', 'e6f7f7', '286c74'),
      theme: { start: '#8ecad0', end: '#4c8f99', ink: '#10292d' },
    },
    {
      id: 'route-wise',
      slug: 'route-wise',
      title: 'Route Wise',
      eyebrow: 'Smart mobility',
      summary:
        'A location-first mobile product for planning journeys, saving places and receiving relevant trip updates in realtime.',
      description:
        'Route Wise combines place search, route planning and saved destinations in a focused mobile workflow. Location updates and trip alerts are designed to remain useful without distracting the traveler.',
      kind: 'published',
      platform: 'Android',
      platforms: ['Android'],
      tags: ['Flutter', 'Google Maps', 'Riverpod'],
      features: [
        'Interactive route planning with map previews',
        'Saved places and recent journey shortcuts',
        'Context-aware trip notifications',
        'Low-connectivity friendly cached destinations',
      ],
      featured: true,
      order: 3,
      stores: [
        { kind: 'play-store', label: 'Play Store', url: 'https://play.google.com/store/apps' },
      ],
      liveUrl: '',
      githubUrl: '',
      ...projectMedia('Route Wise', 'fff4cc', '8c5c05'),
      theme: { start: '#f4cf65', end: '#e9a833', ink: '#31200a' },
    },
    {
      id: 'fin-track',
      slug: 'fin-track',
      title: 'Fin Track',
      eyebrow: 'Personal finance',
      summary:
        'A friendly money dashboard for recording expenses, setting monthly limits and understanding everyday spending patterns.',
      description:
        'Fin Track turns routine transactions into useful spending insights. Users can create budgets, follow category trends and review a clear monthly summary from a secure cross-platform Flutter experience.',
      kind: 'published',
      platform: 'Android & iOS',
      platforms: ['Android', 'iOS'],
      tags: ['Flutter', 'Firestore', 'Charts', 'Biometrics'],
      features: [
        'Fast income and expense capture',
        'Category budgets with progress alerts',
        'Monthly analytics and trend charts',
        'Biometric access for private financial data',
      ],
      featured: true,
      order: 4,
      stores: [
        { kind: 'play-store', label: 'Play Store', url: 'https://play.google.com/store/apps' },
        { kind: 'app-store', label: 'App Store', url: 'https://apps.apple.com/' },
      ],
      liveUrl: '',
      githubUrl: '',
      ...projectMedia('Fin Track', 'e8f5ec', '27663b'),
      theme: { start: '#8bc5a0', end: '#3b8a59', ink: '#12301c' },
    },
    {
      id: 'clean-arch-flutter-package',
      slug: 'clean-arch-flutter-package',
      title: 'Clean Architecture Starter Package',
      eyebrow: 'Open Source Package',
      summary:
        'A production-ready Flutter package and starter kit following Clean Architecture, GetX state management, and modular feature setup.',
      description:
        'An open-source Flutter package providing reusable architectural patterns, state managers, and network helpers for rapid cross-platform application development.',
      kind: 'open-source',
      platform: 'Android & iOS & Web',
      platforms: ['Android', 'iOS', 'Web'],
      tags: ['Flutter', 'Dart', 'Clean Architecture', 'GetX', 'Pub.Dev'],
      features: [
        'Modular feature folder organization',
        'Pre-configured dark/light theme tokens',
        'Reusable HTTP client with automatic retry logic',
        'Comprehensive documentation and example apps',
      ],
      featured: true,
      order: 5,
      stores: [
        { kind: 'pub-dev', label: 'Pub.Dev', url: 'https://pub.dev' },
        { kind: 'github', label: 'Github', url: 'https://github.com/the-razib' },
        { kind: 'web', label: 'Website', url: 'https://labibur.web.app' },
      ],
      liveUrl: 'https://pub.dev',
      githubUrl: 'https://github.com/the-razib',
      ...projectMedia('Clean Arch Package', 'e8f0fe', '1a73e8'),
      theme: { start: '#669df6', end: '#1a73e8', ink: '#0d2d6c' },
    },
    {
      id: 'focus-board',
      slug: 'focus-board',
      title: 'Focus Board',
      eyebrow: 'Personal productivity',
      summary:
        'A fast offline-first task workspace with smart filters, focus sessions and cloud synchronization across devices.',
      description:
        'Focus Board is an offline-first productivity experiment built around quick capture and distraction-free focus sessions. Optional cloud sync keeps selected workspaces available across devices.',
      kind: 'personal',
      platform: 'Android & iOS',
      platforms: ['Android', 'iOS'],
      tags: ['Flutter', 'Isar', 'Cloud Sync'],
      features: [
        'Offline task capture and organization',
        'Focused work timer and session history',
        'Smart filters for priority and due dates',
        'Optional multi-device cloud synchronization',
      ],
      featured: false,
      order: 5,
      stores: [],
      liveUrl: '',
      githubUrl: '',
      ...projectMedia('Focus Board', 'eeeafd', '4f3a96'),
      theme: { start: '#9887d8', end: '#6954b3', ink: '#1d1733' },
    },
    {
      id: 'ai-meal-planner',
      slug: 'ai-meal-planner',
      title: 'AI Meal Planner',
      eyebrow: 'AI experiment',
      summary:
        'An adaptive meal-planning concept that turns preferences and pantry items into practical weekly suggestions.',
      description:
        'AI Meal Planner explores how guided generative workflows can create practical weekly menus from dietary preferences, household size and ingredients already available at home.',
      kind: 'personal',
      platform: 'Flutter Web',
      platforms: ['Web'],
      tags: ['Flutter', 'Generative AI', 'Firebase'],
      features: [
        'Preference and dietary restriction onboarding',
        'Pantry-aware weekly meal suggestions',
        'Editable grocery list generation',
        'Saved plans synchronized with Firebase',
      ],
      featured: false,
      order: 6,
      stores: [{ kind: 'web', label: 'Live Demo', url: 'https://labibur.web.app' }],
      liveUrl: '',
      githubUrl: '',
      ...projectMedia('AI Meal Planner', 'ebf8e5', '356f38'),
      theme: { start: '#93c878', end: '#4f9852', ink: '#17311a' },
    },
    {
      id: 'learn-loop',
      slug: 'learn-loop',
      title: 'Learn Loop',
      eyebrow: 'Learning companion',
      summary:
        'A personal learning tracker for building study streaks, organizing resources and reviewing weekly progress.',
      description:
        'Learn Loop is a lightweight study companion focused on repeatable habits. It organizes learning topics, resource bookmarks and daily check-ins into a clear weekly progress view.',
      kind: 'personal',
      platform: 'Android',
      platforms: ['Android'],
      tags: ['Flutter', 'Riverpod', 'SQLite', 'Notifications'],
      features: [
        'Topic-based study plan organization',
        'Daily check-ins and learning streaks',
        'Saved articles, videos and course resources',
        'Weekly progress and consistency review',
      ],
      featured: false,
      order: 7,
      stores: [],
      liveUrl: '',
      githubUrl: '',
      ...projectMedia('Learn Loop', 'e7f1ff', '245a9b'),
      theme: { start: '#83b4ee', end: '#477fc4', ink: '#132a47' },
    },
  ],
  skills: [
    {
      id: 'mobile-development',
      title: 'Mobile Development',
      icon: 'smartphone',
      skills: ['Flutter', 'Dart', 'iOS', 'Android'],
      order: 1,
    },
    {
      id: 'state-management',
      title: 'State Management',
      icon: 'zap',
      skills: ['Riverpod', 'BLoC', 'GetX'],
      order: 2,
    },
    {
      id: 'backend-cloud',
      title: 'Backend & Cloud',
      icon: 'cloud',
      skills: ['Firebase', 'Firestore', 'FCM', 'REST APIs'],
      order: 3,
    },
    {
      id: 'realtime-ai',
      title: 'Real-time & AI',
      icon: 'bot',
      skills: ['Agora', 'AI Integration', 'Image Analysis'],
      order: 4,
    },
    {
      id: 'payments-maps',
      title: 'Payments & Maps',
      icon: 'credit-card',
      skills: ['Stripe', 'Google Maps SDK', 'Places API'],
      order: 5,
    },
    {
      id: 'devops-tools',
      title: 'DevOps & Tools',
      icon: 'wrench',
      skills: ['Git', 'GitHub', 'Play Store', 'App Store'],
      order: 6,
    },
  ],
  experience: [
    {
      id: 'flutter-developer',
      role: 'Flutter Developer',
      company: 'Product & Client Projects',
      period: '2025 — Present',
      location: 'Dhaka, Bangladesh',
      bullets: [
        'Build and maintain production Flutter applications',
        'Design scalable features with clean architecture',
        'Integrate Firebase, REST APIs and realtime services',
        'Improve performance, reliability and release quality',
      ],
      order: 1,
    },
    {
      id: 'flutter-engineer',
      role: 'Flutter Engineer',
      company: 'Independent Development',
      period: '2024 — 2025',
      location: 'Remote',
      bullets: [
        'Delivered responsive app interfaces from product requirements',
        'Created reusable design systems and state-management foundations',
        'Prepared Android and iOS builds for production distribution',
      ],
      order: 2,
    },
  ],
  education: [
    {
      id: 'computer-science',
      degree: 'Computer Science & Engineering',
      institution: 'Academic background',
      period: 'Ongoing learning journey',
      result: 'Software Engineering',
      order: 1,
    },
    {
      id: 'professional-learning',
      degree: 'Professional Flutter Development',
      institution: 'Project-based practice',
      period: '2024 — Present',
      result: 'Production Delivery',
      order: 2,
    },
    {
      id: 'continuous-learning',
      degree: 'Continuous Engineering Learning',
      institution: 'Documentation & community',
      period: 'Always active',
      result: 'Architecture & UX',
      order: 3,
    },
  ],
};
